import { TeamsContextToNamespaceMap, getQueryParamDictionary, getSignUpQueryParamsDictionary, getSourceAppFromUrl, getUserContext } from '../../../../web/utils/statelessUtil';
import { SourceApps } from '../../../models/constants/constants';
import { FeedbackSurveyCategory } from './feedbackModels';
import { EduProductId, EduQueryParameter } from '../../../../web/edu/eduConstants';
import { resolveQueryParameterValue } from '../../../../web/edu/eduHelpers';

// ExtensionAttributes keys
export enum ExtensionAttributeKeys {
  IsAAD = 'IsAAD',
  UserObjectId = 'UserObjectId',
  Theme = 'Theme',
  AimId = 'AimId',
  AppName = 'AppName',
  BucketId = 'BucketId',
  IsMmdUser = 'IsMmdUser',
  ThemeL1 = 'ThemeL1',
  ThemeL2 = 'ThemeL2',
  ThemeL3 = 'ThemeL3',
  TimezoneOffsetInMinutes = 'TimezoneOffsetInMinutes',
  WaitTime = 'WaitTime',
  IsEdu = 'IsEdu',
  IsoCallingCode = 'IsoCallingCode',
  PremierEntitlementId = 'PremierEntitlementId',
  IsPremierUnified = 'IsPremierUnified',
  IsUnifiedEnterprise = 'IsUnifiedEnterprise',
  EduProductName = 'product_name',
  EduPlatform = 'platform',
  EduClientName = 'client_name',
  EduProductFeatureArea = 'product_feature_area',
  EduAppVersion = 'app_version',
  EduErrorCode = 'error_code',
  EduRing = 'ring',
  EduAccountType = 'account_type',
  EduOsVersion = 'os_version',
  EduBrowserVersion = 'browser_version',
  EduCorrelationId = 'correlation_id',
  EduResourceId = 'resource_id',
  EduMeetingId = 'meeting_id',
  EduLanguage = 'language',
  EduLms = 'lms',
  EduCountryCode = 'country_code',
  EduUserImpact = 'user_impact',
  EduNewSdsCustomer = 'new_customer',
  TaggedProduct = "TaggedProduct",
  ClientContext = "ClientContext",
}

// MetaDatas keys
export enum MetaDataKeys {
  AppKeyName = 'AppKeyName',
  AssetId = 'AssetId',
  Language = 'Language',
  Country = 'Country',
  TeamsNamespace = 'TeamsNamespace',
  UserImpact = 'UserImpact',
  UserRole = 'UserRole',
  IsPayPerIncidentExperiment = 'IsPayPerIncidentExperiment',
  IsAgentAssistSubscriptionSignup = 'IsAgentAssistSubscriptionSignup',
}

// Rave MetaData type, using Pascal case for Key/Value to align with Rave
export type RaveMetaData = { Key: string, Value: string };

// RequestModality
export enum RequestModality {
  // To handle undefined modality because it can be optional.
  // Note that this does not map with Rave model and we should not send it to Rave.
  Undefined = 0,

  // The Freelance Partner is required to immediately create a Session and call the customer.
  Phone = 1,

  // The Freelance Partner can contact the customer via email.
  Email = 2,

  // The Request came from the IVR system.
  IVR = 3,

  // The Freelance Partner can contact the customer via chat only
  Chat = 4,

  // The Request came from the IVR C2C.
  C2C = 5
}

// RequestSeverity
export enum RequestSeverity {
  // Non critical issue used by unified premier tenants
  // Means non critical issues
  None = 0,

  // Critical issue used by unified premier tenants
  // Means critical issues
  Critical = 1,

  // Changed it to Sev1 to align with Support Central
  // Critical issue used by unified premier tenants
  // Means critical issues
  Sev1 = 2,

  // Critical issue SevA used by non unified premier tenants
  // Means critical issues
  SevA = 3,

  // Non Critical issue SevB used by non unified premier tenants
  // Means non critical issues
  SevB = 4,

  // Non Critical issue SevC used by non unified premier tenants
  // Means non critical issues with longer SLA
  SevC = 5
}

// The FreelanceRequest model
export class FreelanceRequest {
  // Request Id
  public RequestId: string;

  // association Id in DTM
  public WorkspaceId: string;

  // Request State
  public State: string;

  // MSSolve Ticket Number
  public MSSolveTicketNumber: string = String();

  // Whether partner closed the request
  public IsFeedbackClosed = false;

  // Created DateTime
  public CreateDateTime: string;

  // Notified DateTime
  public NotifiedDateTime: string;

  // Acknowledged DateTime
  public AcknowledgeDateTime: string;

  // Cancelled DateTime
  public CanceledDateTime: string;

  // Closed DateTime
  public ClosedDateTime: string;

  // Complete DateTime
  public CompletedDateTime: string;

  // Customer feedback DateTime
  public CustomerFeedbackDateTime: string;

  // Survey Category list of influencers that have impacted the users experience while trying to get their issue resolved
  public SurveyCategories: FeedbackSurveyCategory[] = [];

  // Service closed DateTime
  public ServiceClosedDateTime: string;

  // Partner id
  public PartnerId: string;

  // Support area Id
  public SupportAreaId: string;

  // Support area name
  public SupportAreaName: string;

  // Support area step name
  public SupportAreaStepName: string;

  // User Puid
  public UserPuid: string;

  // Tenant Id
  public TenantId: string;

  // User first name
  public UserFirstName: string;

  // User last name
  public UserLastName: string;

  // User phone
  public UserPhone: string;

  // User phone dialing code index from the RegionInfo response
  public UserPhoneDialingCodeIndex: string;

  // User phone dialing code
  public UserCountryDialingCode: string;

  // User phone without dialing code
  public UserPhoneWithoutDialingCode: string;

  // two digit ISO dialing code
  public TwoDigitCountryCode: string;

  // User email
  public UserEmail: string;

  // User Lcid
  public UserLcid: number;

  // A Screensharing session url
  public SessionUrl: string;

  // Screen sharing session type
  public SessionType: string;

  // Screen sharing session id
  public SessionId: string;

  // Lync conference Phone
  public PhoneNumber: string;

  // Lync conference Passcode
  public PhonePasscode: string;

  // Partner delegation availability
  public IsDelegated: string;

  // MS Support Ticket
  // ReSharper disable once InconsistentNaming
  public MSSupportTicket: string;

  // IsResolved Flag
  public IsResolved: string;

  // Request rating
  public Rating: string;

  // Request comment
  public Comment: string;

  // The request extension attributes
  public ExtensionAttributes: { [key: string]: string };

  // Row version
  public RowVersion: string;

  // Source
  public Source: string;

  // Modality
  public Modality: RequestModality;

  // Parature Ticket Number
  public ParatureTicketNumber: string;

  // Title
  public Title: string;

  // User description
  public UserDescription: string;

  // Severity
  public Severity?: RequestSeverity;

  // The reopen reason
  public ReopenReason: string;

  // MetaDatas
  public MetaDatas: RaveMetaData[];

  // Attachment
  public Attachments: AttachmentData[];

  // CustomerContact
  public CustomerContacts: CustomerContact[];

  // Notes
  public Notes: Note[];

  // Secondary ticket number
  public SecondaryTicketNumber?: string;

  // Secondary ticket type
  public SecondaryTicketType?: CommunicationRequestType;

  // Request clientContext
  public ClientContext: { [key: string]: string };

  public constructor() {
    this.ExtensionAttributes = {
      [ExtensionAttributeKeys.TimezoneOffsetInMinutes]: (new Date()).getTimezoneOffset().toString(),
    };

    this.MetaDatas = [];
    this.Attachments = [];
    this.CustomerContacts = [];
    this.Notes = [];
    const assetId = (window.location.href as string).match(new RegExp('assetid=([^&]+)', 'i'));
    if (!!assetId && assetId.length === 2 && !!assetId[1]) {
      this.MetaDatas.push({ Key: MetaDataKeys.AssetId, Value: assetId[1] });
    }
    const sourceApp = getSourceAppFromUrl(window.location.href);
    const userContextObj = getUserContext();
    // add IsEdu when initialize
    if (sourceApp === SourceApps.Teams || sourceApp === SourceApps.Teams2 || sourceApp === SourceApps.TeamsAdmin || sourceApp === SourceApps.TeamsAdmin2) {
      const teamsTenantSKU = (userContextObj && userContextObj.TeamsTenantSKU) ? userContextObj.TeamsTenantSKU.toLowerCase() : null;
      if (teamsTenantSKU === 'edu' || teamsTenantSKU === 'education') {
        this.ExtensionAttributes[ExtensionAttributeKeys.TaggedProduct] = `edu_${EduProductId.Teams}`;
        this.ExtensionAttributes[ExtensionAttributeKeys.IsEdu] = 'true';
        this.MetaDatas = this.MetaDatas.concat(
          !!userContextObj ? Object.keys(userContextObj).map(key => ({ Key: key, Value: userContextObj[key] })) : []
        );
      }
      if (!!TeamsContextToNamespaceMap(userContextObj)) {
        this.MetaDatas.push({ Key: MetaDataKeys.TeamsNamespace, Value: TeamsContextToNamespaceMap(userContextObj) });
      }
    }
    // add context to metaDatas for OneDriveWeb
    if (sourceApp === SourceApps.OneDriveWeb) {
      this.MetaDatas = this.MetaDatas.concat(
        !!userContextObj ? Object.keys(userContextObj).map(key => ({ Key: key, Value: userContextObj[key] })) : []
      );
    }

    // add Signup metadata on initialize
    if (sourceApp === SourceApps.SignUp) {
      const signUpQueryParams = getSignUpQueryParamsDictionary();
      if (!!signUpQueryParams) {
        const metaData = Object.keys(signUpQueryParams)
          .map((key: string) => {
            return { Key: key, Value: signUpQueryParams[key] };
          });
        this.MetaDatas = this.MetaDatas.concat(metaData);
      }
    }

    if (sourceApp === SourceApps.Edu) {
      const queryParams = getQueryParamDictionary(window.location.href);
      const productId = queryParams[EduQueryParameter.ProductId];
      this.ExtensionAttributes[ExtensionAttributeKeys.TaggedProduct] = `edu_${productId}`;

      for (const paramKey of Object.values(EduQueryParameter)) {
        const value = resolveQueryParameterValue(paramKey, queryParams[paramKey])
        const extensionAttributeKey = EduQueryParamsToExtensionAttributes[paramKey];
        if (value) {
          this.ExtensionAttributes[extensionAttributeKey] = value;
        }
      }
    }
  }
}

// Determines which Request property the RequestProperty component will show
export enum RequestPropertyKey {
  State,
  AcknowledgeDateTime,
  CreateDateTime,
  ClosedDateTime,
  Severity,
  Title,
  TaggedProduct,
  ParatureTicketNumber,
  UserFullName,
  PhoneNumber,
  Email,
  UserDescription,
  Tenant
}
// UI State based on what we should SHOW the customer in our ux
export enum RequestUIState {
  Opened = 0,
  AgentAssigned = 1,
  InProgress = 2,
  Closed = 3,
  Cancelled = 4,
  Feedback = 5,
  Escalated = 6
}

export enum RequestEscalationState {
  // / Not escalated
  NotEscalated = '0',

  // / Escalation is in progess
  Escalating = '1',

  // / Escalation is complete
  Escalated = '2'
}

export enum RequestStatusStep {
  // / <summary>
  // /  Request Status: Opened
  // / </summary>
  Opened = 1,

  // / <summary>
  // /  Request Status: Acknowledged
  // / </summary>
  Acknowledged = 2,

  // / <summary>
  // /  Request Status: InProgress
  // / </summary>
  InProgress = 3,

  // / <summary>
  // /  Request Status: Escalating
  // / </summary>
  Escalating = 4,

  // / <summary>
  // /  Request Status: Escalated
  // / </summary>
  Escalated = 5,

  // / <summary>
  // /  Request Status: Resolved
  // / </summary>
  Resolved = 6
}

export enum RequestState {
  // Active Request
  Active = 1,
  // Notified Request (deprecated)
  Notified = 2,
  // Acknowledged Request
  Acknowledged = 3,
  // PartnerCalled Request (deprecated)
  PartnerCalled = 4,
  // Rejected Request (deprecated)
  Rejected = 5,
  // Escalated Request (deprecated)
  Escalated = 6,
  // CustomerWithdrew Request (deprecated)
  CustomerWithdrew = 7,
  // CustomerClosed Request (deprecated)
  CustomerClosed = 8,
  // Closed Request
  Closed = 9,
  // Customer Cancelled Request
  Cancelled = 10,
  // Partner Completed Request
  Completed = 11,
  // Delayed, used when ticket has a scheduled call back
  Delayed = 12,
  // Unknown State
  Unknown = 500
  // phone tag changes - when the customer hits I'm available now button after waiting for the agent call
  // RequestForCallReceivedren =
}

// / <summary>
// / Customer-facing ticket state transitions
// / </summary>
export enum CustomerSupportRequestState {
  // / <summary>
  // / Acknowledged (active) tickets
  // / </summary>
  Active = 3,

  // / <summary>
  // / Closed (needing feedback) tickets
  // / </summary>
  NeedFeedback = 9,

  // / <summary>
  // / Completed tickets
  // / </summary>
  Completed = 11,

  // / <summary>
  // / All tickets
  // / </summary>
  All = 99
}

// / <summary>
// / Helps to filter tickets created by tenant admin or tenant
// / </summary>
export enum RequestCreatedBy
{
  // / <summary>
  // / Tickets created by tenant admin
  // / </summary>
  TenantAdmin,

  // / <summary>
  // / Tickets created by tenant
  // / </summary>
  Tenant
}

export class RequestStepsSchema {
  public Id!: number;
  public Title = '';
  public ContentText = '';
  public ContentSubText = '';
  public PhoneNumber = '';
  public LinkText = '';
  public Date = '';
  public Active = false;
  public IsStepInProcess = false;
}

export class AttachmentData {
  // / CwsId
  public CwsId: string;

  // / The Name of the attachment
  public Name: string;

  // / The DateTime when the Attachment was inserted
  public CreateDateTime: string;

  // / The Content Location
  public ContentLocation: string;

  // / The Content Type
  public ContentType: string;

  // / The Content Size
  public Size: number;

  // / The associated requestid
  public RequestId: string;

  // / The Dtm workspace Id
  public WorkspaceId: string;

  // / Tenant Id
  public TenantId: string;

  // / The Location
  public Location: string;
}

export class Attachment {
  // / AttachmentData
  public AttachmentData: AttachmentData;

  // / Is upload in progress
  public IsUploadAttachmentInProgress: boolean;
}

export enum AttachmentsConstants {
  ChunkTag = '_chunk_',
  MsgExtension = '.msg',
  MsgMimeType = 'application/vnd.ms-outlook',
  FallbackMimeType = 'application/octet-stream',
}

export enum ErrorCodes {
  None = 0,
  AttachmentFileSizeError = 1,
  AttachmentFileNameError = 2,
  AttachmentDuplicatedFileNameError = 3,
  AttachmentFileExtensionError = 4,
  AttachmentUploadFileError = 5,
  AttachmentEmptyFileError = 6
}

export class CustomerContact {
  public PrimaryContact: boolean;
  public Email: string;
  public PhoneNumber: string;
  public DialingCode: string;
}

export class Note {
  // / Time
  public Time: string;

  // / Note Text
  public Text: string;
}

export enum CommunicationRequestType {
  // cws Request number
  Cws = 0,

  // Parature Request number
  Parature = 1,

  // MSSupport Request number
  MSSupportTicket = 2,

  // CritSit Request number.
  CritSit = 3,

  // MS Solve Request number.
  MSaaS = 4,

  // MSaaS Premier Request number.
  MSaaSPremier = 5,

  // MSaaS CAICollaboration Ticket type.
  MSaaSCAICollaboration = 6,

  // Snow Ticket type.
  Snow = 7,
}

export class UpdateContactsResponseModel {
  public IsUpdateContactsSuccessful: boolean;
  public IsUpdateExtensionAttributesSuccessful: boolean;
}

const EduQueryParamsToExtensionAttributes = {
  [EduQueryParameter.ProductId]: ExtensionAttributeKeys.EduProductName,
  [EduQueryParameter.PlatformId]: ExtensionAttributeKeys.EduPlatform,
  [EduQueryParameter.ClientId]: ExtensionAttributeKeys.EduClientName,
  [EduQueryParameter.ProductFeatureAreaId]: ExtensionAttributeKeys.EduProductFeatureArea,
  [EduQueryParameter.AppVersion]: ExtensionAttributeKeys.EduAppVersion,
  [EduQueryParameter.ErrorCode]: ExtensionAttributeKeys.EduErrorCode,
  [EduQueryParameter.Ring]: ExtensionAttributeKeys.EduRing,
  [EduQueryParameter.ResourceId]: ExtensionAttributeKeys.EduResourceId,
  [EduQueryParameter.MeetingId]: ExtensionAttributeKeys.EduMeetingId,
  [EduQueryParameter.LanguageCode]: ExtensionAttributeKeys.EduLanguage,
  [EduQueryParameter.LmsId]: ExtensionAttributeKeys.EduLms,
};